import { isVendorMode } from '../Vendor';
import {
  UserAuth,
  UserAuthFeatures,
  UserAuthTmpFeatureConfig,
} from '../../definitions/userAuth';
import OrganizationsStore from '../../stores/observables/OrganizationsStore';

export const OrganizationFlagEnum = {
  CRM_INFO_SEARCH: 'crmInfoSearch',
  CRM_INTEGRATION: 'crmIntegrationFeature',
  CRM_ABSTRACTION_MS_DYNAMICS: 'featureCrmAbstractionMsDynamics',
  CONSENT_DECLINED_SUPPRESSION: 'consentDeclinedSuppression',
  DISABLE_SORT: 'disableSort',
  DISABLE_DEAL_CREATION: 'disableDealCreation',
  DISABLE_COMPANY_CREATION: 'disableCompanyCreation',
  AUTO_ROOM_CREATION: 'autoRoomCreation',
  DEFAULT_CONTENT_DOWNLOAD: 'defaultContentDownload',
  FEDERATED_ASSET_ENABLED: 'federatedAssetEnabled',
  RELEASE_CONTROL_FLAG_ASSET_PICKER: 'releaseControlFlagAssetPicker',
  INLINE_IFRAME_VIEWER: 'inlineIframeViewer',
  ENFORCED_ALLOWED_VIEWERS_ONLY: 'enforcedAllowedViewersOnly',
  ENFORCED_REQUIRED_PIN: 'enforcedRequiredPin',
  ENFORCED_REQUIRED_EMAIL: 'enforcedRequiredEmail',
  EDIT_COPY: 'editCopy',
  EMAIL_SENDER_CONFIGURABLE: 'emailSenderConfigurable',
  FORCE_ROOMS_DEAL_CREATION: 'forceRoomsDealCreation',
  ALLOW_SKIP_DEAL: 'allowSkipDeal',
  INSIGHTS_OPTIMIZATION_V2: 'insightsOptimizationV2',
  GONG_STORAGE_PROVIDER: 'gongStorageProvider',
  HIDE_POWERED_BY: 'hidePoweredBy',
  INHOUSE_CONSENT: 'inhouseConsent',
  INHOUSE_SIGNER: 'inhouseSigner',
  INSIGHTS_CONTENT_WON_DEALS: 'insightsContentWonDeals',
  MUTUAL_ACTION_PLANS: 'mutualActionPlans',
  NEW_PDF_VIEWER: 'newPdfViewer',
  FEATURE_FLAG_SUPPORT_MULTICURRENCY_SFDC:
    'featureFlagSupportMulticurrencySfdc',
  OPEN_AI: 'openAi',
  REFERENCE_MANAGEMENT: 'referenceManagement',
  RELEASE_CONTROL_PARTNER_SETTINGS: 'releaseControlPartnerSettings',
  RELEASE_CONTROL_BUYER_COPILOT: 'releaseControlBuyerCopilot',
  RELEASE_CONTROL_DSR_FOLDERS: 'releaseControlDsrFolders',
  RELEASE_CONTROL_RESTRICT_USER_VISIBILITY:
    'releaseControlRestrictUserVisibility',
  RING_CENTRAL: 'ringCentral',
  RINGCENTRAL_STORAGE_PROVIDER: 'ringcentralStorageProvider',
  ROOM_BLACKLIST_DOMAINS: 'roomBlacklistDomains',
  SHARED_LINKS: 'sharedLinks',
  SIGNABLE_TEMPLATES: 'signableTemplates',
  SIGNABLES_FEATURE: 'signablesFeature',
  SMS_NOTIFICATIONS: 'smsNotifications',
  VANITY_URL: 'vanityUrlFeature',
  ZOOM_STORAGE_PROVIDER: 'zoomStorageProvider',
  // rollout flags
  INTERNAL_ROOMS_V2: 'internalRoomsV2',
} as const;

export const UserAuthFlagEnum: Record<string, keyof UserAuthFeatures> = {
  INTERNAL_ROOMS: 'dsrInternalRooms',
  ENABLE_DIGITAL_SALES_PLAY: 'enableDigitalSalesPlay',
  DSP_USER_GROUPS: 'dspUserGroups',
} as const;

export const UserAuthTmpFeatureConfigFlagEnum: Record<
  string,
  keyof UserAuthTmpFeatureConfig
> = {
  CUSTOM_EMAIL_TEMPLATES: 'customDSREmailTemplates',
  DSR_DEFAULT_LANGUAGE: 'isDSRDefaultLanguageEnabled',
  ROOM_PUBLISH_EMAIL: 'dsrRoomPublishEmail',
  RELEASE_CONTROL_DSR_SFDC_USER_SCOPING: 'releaseControlDsrSfdcUserScoping',
  HIDE_DSR_DEFAULT_ICONS: 'hideDsrDefaultIcons',
} as const;

export type OrganizationFeatureFlag =
  (typeof OrganizationFlagEnum)[keyof typeof OrganizationFlagEnum];

export type UserAuthFeatureFlag =
  (typeof UserAuthFlagEnum)[keyof typeof UserAuthFlagEnum];

export type UserAuthTmpFeatureConfigFlag =
  (typeof UserAuthTmpFeatureConfigFlagEnum)[keyof typeof UserAuthTmpFeatureConfigFlagEnum];

/**
 * Gets the value of the feature flag from the organization.
 * @note this is not supposed to be used to check for feature flags,
 * use `useFeatureFlag` instead
 */
export const getOrganizationFlag = (flag: OrganizationFeatureFlag) => {
  const currentOrg = OrganizationsStore.getCurrentOrganization();
  return (currentOrg && currentOrg[flag]) || false;
};

/**
 * Gets the value of the feature flag from the given user auth information.
 * @note this is not supposed to be used to check for feature flags,
 * use `useFeatureFlag` instead
 */
export const getUserAuthFlag = (
  flag: UserAuthFeatureFlag,
  userAuth: UserAuth
): boolean => {
  const value =
    userAuth.company?.companySettings?.[flag] || userAuth.features?.[flag];
  return typeof value === 'boolean' ? value : false;
};

/**
 * Gets an array value from user auth feature flags (e.g., dspUserGroups)
 */
export const getUserAuthFlagArray = (
  flag: UserAuthFeatureFlag,
  userAuth: UserAuth
): string[] => {
  const value =
    userAuth.company?.companySettings?.[flag] || userAuth.features?.[flag];
  return Array.isArray(value) ? value : [];
};

/**
 * Gets the value of the extra config flag from the given user auth information.
 * @note this is not supposed to be used to check for feature flags,
 * use `useFeatureFlag` instead.
 * Also, this returns true by default if the flag is not found. It was the case of customDSREmailTemplates flag.
 */

export const getTmpFeatureConfigFlag = (
  flag: UserAuthTmpFeatureConfigFlag,
  userAuth: UserAuth
): boolean => userAuth.tempFeatureConfig?.[flag] || false;

/**
 * Convenience method to check if the deal creation is enforced for room
 * creation or edition flows. This is a combination of being in vendor mode
 * and also the feature flag to force the deal creation is enabled
 */

export const isDealCreationEnforced = () =>
  isVendorMode() &&
  getOrganizationFlag(OrganizationFlagEnum.FORCE_ROOMS_DEAL_CREATION);

/**
 * Convenience method to check if the room company association feature is
 * enabled. This is only available in vendor mode.
 */
export const isRoomCompanyAssociationFeatureEnabled = () => isVendorMode();
